import { useEffect, useState } from "react";
import { Download, Instagram, Facebook, Twitch, Youtube, Music2, Radio, Mail, FileText } from "lucide-react";
import { fetchStats, fetchShow, mediaKitPdfUrl } from "../lib/api";
import { LOGO_PRIMARY, STUDIO_SHOT, SOCIALS, formatNumber } from "../lib/brand";
import SEO from "../components/SEO";

const ICONS = { Instagram, Facebook, Twitch, Youtube, Music2, Radio };

export default function MediaKit() {
  const [stats, setStats] = useState({});
  const [show, setShow] = useState(null);

  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
    fetchShow().then(setShow).catch(() => {});
  }, []);

  const platformStats = [
    { key: "tiktok", label: "TikTok", value: stats.tiktok },
    { key: "instagram", label: "Instagram", value: stats.instagram },
    { key: "facebook", label: "Facebook", value: stats.facebook },
    { key: "twitch", label: "Twitch", value: stats.twitch },
    { key: "youtube", label: "YouTube", value: stats.youtube },
    { key: "spotify", label: "Spotify", value: stats.spotify },
  ];

  return (
    <div data-testid="media-kit-page">
      <SEO
        title="Media Kit · Press & Partnerships"
        description="Show bio, host credentials, metric dashboard across all 6 platforms, and a downloadable PDF. Everything working press, brand partners, and bookers need."
        image={LOGO_PRIMARY}
      />
      {/* Hero header */}
      <section className="border-b-2 border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)] relative overflow-hidden noise-overlay">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 md:py-24 relative">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-3">
              <div className="text-kicker text-[var(--rust)]">Press & Partnerships</div>
              <h1 className="text-headline text-[clamp(2.5rem,8vw,6.5rem)] mt-2 leading-[0.95]">
                The Media Kit
              </h1>
              <p className="font-serif-ed text-xl md:text-2xl text-[var(--paper-deep)]/85 mt-5 max-w-xl italic">
                Everything working press, brand partners, and bookers need — show bio, host credentials, and total reach.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href={mediaKitPdfUrl}
                   target="_blank" rel="noreferrer"
                   className="btn-rust"
                   data-testid="media-kit-download-pdf">
                  <Download size={16} /> Download PDF
                </a>
                <a href="mailto:press@unfilteredandonball.com" className="btn-ghost text-[var(--paper)] border-[var(--paper)] hover:bg-[var(--paper)] hover:text-[var(--ink)]" data-testid="media-kit-email-press">
                  <Mail size={14} /> Email Press
                </a>
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="bg-[var(--paper)] p-6 border-2 border-[var(--rust)] shadow-[8px_8px_0_var(--rust)]">
                <img src={LOGO_PRIMARY} alt="Unfiltered & On-Ball" loading="eager" className="w-full" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Bio */}
      <section className="py-16 border-b-2 border-[var(--ink)]" data-testid="mission-section">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-14">
          <div>
            <div className="text-kicker">The Mission</div>
            <h2 className="text-headline text-4xl md:text-5xl mt-2 mb-5">Why We Go <span className="text-[var(--rust)]">Unfiltered</span>.</h2>
            <p className="font-serif-ed text-lg leading-relaxed text-[var(--ink-soft)]">
              {show?.mission || "Loading…"}
            </p>
          </div>
          <div>
            <div className="text-kicker">Show Bio</div>
            <h2 className="text-headline text-4xl md:text-5xl mt-2 mb-5">Built for the <span className="text-[var(--rust)]">group chat</span>.</h2>
            <p className="font-serif-ed text-lg leading-relaxed text-[var(--ink-soft)]">
              {show?.bio || "Loading…"}
            </p>
          </div>
        </div>
      </section>

      {/* Metric Dashboard */}
      <section className="py-16 border-b-2 border-[var(--ink)] bg-[var(--paper-deep)]/40" data-testid="metric-dashboard">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between pb-4 mb-10 border-b-2 border-[var(--ink)]">
            <div>
              <div className="text-kicker">Metric Dashboard</div>
              <h2 className="text-headline text-4xl md:text-6xl">Total Reach</h2>
            </div>
            <div className="hidden md:block text-right font-mono-ed text-xs uppercase tracking-widest text-[var(--muted)]">
              Across 6 platforms<br />Updated {stats.updated_at ? new Date(stats.updated_at).toLocaleDateString() : "regularly"}
            </div>
          </div>

          {/* Top row: big headline stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="sticker-card p-8" data-testid="stat-monthly-listeners">
              <div className="text-kicker">Monthly Listeners</div>
              <div className="stat-number text-[var(--ink)] mt-2">{formatNumber(stats.avg_monthly_listeners)}</div>
              <div className="text-dek text-sm mt-2">Average across all streaming platforms</div>
            </div>
            <div className="sticker-card p-8" data-testid="stat-total-downloads">
              <div className="text-kicker">Total Downloads</div>
              <div className="stat-number text-[var(--rust)] mt-2">{formatNumber(stats.total_downloads)}</div>
              <div className="text-dek text-sm mt-2">Lifetime podcast downloads</div>
            </div>
            <div className="sticker-card p-8" data-testid="stat-episodes">
              <div className="text-kicker">Episodes Shipped</div>
              <div className="stat-number text-[var(--ink)] mt-2">{show?.episodes || "40+"}</div>
              <div className="text-dek text-sm mt-2">Weekly drops, zero filler</div>
            </div>
          </div>

          {/* Platform grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="platform-grid">
            {platformStats.map((p) => {
              const s = SOCIALS.find((x) => x.key === p.key);
              const Ic = s && ICONS[s.icon] ? ICONS[s.icon] : Music2;
              return (
                <a key={p.key} href={s?.url} target="_blank" rel="noreferrer"
                   className="border-2 border-[var(--ink)] bg-[var(--paper)] p-6 hover:shadow-[6px_6px_0_var(--rust)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition flex items-center justify-between"
                   data-testid={`platform-${p.key}`}>
                  <div>
                    <div className="flex items-center gap-2 text-kicker">
                      <Ic size={14} /> {p.label}
                    </div>
                    <div className="stat-number text-3xl md:text-4xl mt-2">{formatNumber(p.value)}</div>
                    <div className="font-mono-ed text-xs uppercase tracking-widest text-[var(--muted)] mt-2">
                      @Unfilteredandonball
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hosts */}
      <section className="py-16 border-b-2 border-[var(--ink)]" data-testid="hosts-section">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="text-kicker">The Hosts</div>
          <h2 className="text-headline text-4xl md:text-6xl mt-2 mb-12">Two players. One <span className="text-[var(--rust)]">mic</span>.</h2>

          <div className="grid md:grid-cols-2 gap-10">
            {(show?.hosts || []).map((h, i) => (
              <div key={h.name} className="sticker-card p-8" data-testid={`host-card-${i}`}>
                <div className={`w-20 h-20 border-2 border-[var(--ink)] flex items-center justify-center font-display text-4xl ${i === 0 ? "bg-[var(--rust)] text-[var(--paper)]" : "bg-[var(--ink)] text-[var(--paper)]"}`}>
                  {h.name.charAt(0)}
                </div>
                <h3 className="text-headline text-3xl mt-5">{h.name}</h3>
                <div className="text-kicker mt-1">{h.role}</div>
                <p className="font-serif-ed text-base leading-relaxed text-[var(--ink-soft)] mt-4">{h.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience */}
      <section className="py-16 border-b-2 border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" data-testid="audience-section">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-kicker text-[var(--rust)]">The Audience</div>
            <h2 className="text-headline text-4xl md:text-6xl mt-2">Who's <span className="text-[var(--rust)]">tuned in</span>.</h2>
            <p className="font-serif-ed text-lg md:text-xl text-[var(--paper-deep)]/85 mt-5 leading-relaxed">
              {show?.audience || "Loading…"}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="border-2 border-[var(--paper)] p-6">
              <div className="text-kicker text-[var(--rust)]">Age</div>
              <div className="stat-number text-[var(--paper)] mt-2">18–44</div>
            </div>
            <div className="border-2 border-[var(--paper)] p-6">
              <div className="text-kicker text-[var(--rust)]">Male skew</div>
              <div className="stat-number text-[var(--paper)] mt-2">70%</div>
            </div>
            <div className="border-2 border-[var(--paper)] p-6">
              <div className="text-kicker text-[var(--rust)]">Mobile-first</div>
              <div className="stat-number text-[var(--paper)] mt-2">90%</div>
            </div>
            <div className="border-2 border-[var(--paper)] p-6">
              <div className="text-kicker text-[var(--rust)]">Engagement</div>
              <div className="stat-number text-[var(--paper)] mt-2">High</div>
            </div>
          </div>
        </div>
      </section>

      {/* Press contact */}
      <section className="py-16" data-testid="press-contact">
        <div className="max-w-[1000px] mx-auto px-5 md:px-8 text-center">
          <div className="text-kicker">Book It · Pitch It · Quote It</div>
          <h2 className="text-headline text-4xl md:text-6xl mt-2 mb-6">
            Working press? <span className="text-[var(--rust)]">Let's talk.</span>
          </h2>
          <p className="text-dek text-lg max-w-2xl mx-auto mb-8">
            For interviews, partnerships, brand deals, and press inquiries — grab the full PDF or reach us directly.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href={mediaKitPdfUrl} target="_blank" rel="noreferrer" className="btn-rust" data-testid="bottom-download-pdf">
              <FileText size={16} /> Download Full PDF
            </a>
            <a href="mailto:press@unfilteredandonball.com" className="btn-ink">
              <Mail size={16} /> press@unfilteredandonball.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
